import { zoneOpts } from "./shell/tz";

/**
 * Is this instant on the viewer's own calendar day today (or US Eastern's,
 * when the times are shown in it; DSPRT-299)? A time written next
 * to its day ("Sun 1:30 AM") drops the day once the clock reaches it
 * (DSPRT-284): after midnight on Sunday the card reads "1:30 AM", not "Sun".
 */
export function isLocalToday(iso: string | null | undefined): boolean {
  return !!iso && new Date(iso).toLocaleDateString("en-CA", zoneOpts()) === new Date().toLocaleDateString("en-CA", zoneOpts());
}

/* ------------------------------------------------------------------ *
 * A slate's day on the reader's calendar (DSPRT-405, #163). Games are
 * filed under the league's (Eastern) day, and the addresses keep it:
 * /scores/2026-10-14 is that night's slate wherever you are. What the
 * reader is SHOWN as its day is his own: the day its first game tips off
 * where he is. The slate that tips at 2:30 AM Thursday in Jerusalem is
 * Thursday there, as the score strip and the Wire already said, not
 * "today, the 14th"; opening night's 3 PM tip in Detroit is Tuesday 10 PM.
 * In the US, and with the clock set to Eastern, a slate's day is its date.
 *
 * The first tip-offs come with the game-day list (rememberFirstTips). A
 * day without one is named by 7:30 PM in New York, when most nights tip.
 * East of the US a Sunday matinee and the Saturday-night slate before it
 * can both fall on Sunday. Two Sunday tabs read as one day twice and the
 * calendar could reach only the first (B-112), so the later of two slates
 * that land on one day takes the next: its games run into that day anyway.
 * ------------------------------------------------------------------ */

/** "2026-10-15" for an instant, in the reader's zone (or the one given), built from parts so no locale's date order can creep in. */
function isoOn(d: Date, opts: { timeZone?: string }): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit", ...opts })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

/** 7:30 PM in New York on that date: 23:30Z under daylight time, 00:30Z the next day under standard time. */
function slateEvening(iso: string): Date {
  const edt = new Date(`${iso}T23:30:00Z`);
  const hour = Number(edt.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" }));
  return hour === 19 ? edt : new Date(edt.getTime() + 3_600_000);
}

const firstTips = new Map<string, string>();
/** Each listed slate's day, clashes moved on (B-112), for the zone it was worked out in. */
let listedDays: { zone: string; days: Map<string, string> } | null = null;

/** Each game day's first tip-off, as the game-day list gives it. */
export function rememberFirstTips(days: ReadonlyArray<{ date: string; first?: string | null }>): void {
  for (const d of days) if (d.first) firstTips.set(d.date, d.first);
  listedDays = null;
}

/** The day its first tip falls on, or 7:30 PM New York's when it isn't known. */
function firstTipDay(iso: string, opts: { timeZone?: string }): string {
  const first = firstTips.get(iso);
  return isoOn(first ? new Date(first) : slateEvening(iso), opts);
}

const nextIso = (iso: string): string => new Date(Date.parse(`${iso}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/** Every listed slate's day in order, a slate whose day the one before it already took moved to the next. */
function listedDayMap(opts: { timeZone?: string }): Map<string, string> {
  const zone = opts.timeZone ?? "";
  if (listedDays?.zone === zone) return listedDays.days;
  const days = new Map<string, string>();
  let prev: string | null = null;
  for (const iso of [...firstTips.keys()].sort()) {
    let day = firstTipDay(iso, opts);
    if (prev && day <= prev) day = nextIso(prev);
    days.set(iso, day);
    prev = day;
  }
  listedDays = { zone, days };
  return days;
}

/** The reader's date for the slate the league files under `iso`. */
/** The reader's date of one tip-off instant (NBA-08): the slide header says when its listed day was moved on. */
/** The weekday of one tip-off on the reader's clock, short ("Fri"), for a line that would otherwise read as today's. */
export function tipWeekday(tipoff: string): string {
  return new Date(tipoff).toLocaleDateString("en-US", { weekday: "short", ...zoneOpts() });
}

export function tipDay(tipoff: string): string {
  return isoOn(new Date(tipoff), zoneOpts());
}

export function slateDay(iso: string): string {
  const opts = zoneOpts();
  return listedDayMap(opts).get(iso) ?? firstTipDay(iso, opts);
}

/** Today on the reader's calendar (or Eastern's, when the times are shown in it). */
export function localTodayIso(): string {
  return isoOn(new Date(), zoneOpts());
}

/** Whole days from the reader's today to a slate's day: 0 today, 1 tomorrow, -1 yesterday. */
export function slateOffset(iso: string): number {
  return Math.round((Date.parse(`${slateDay(iso)}T12:00:00Z`) - Date.parse(`${localTodayIso()}T12:00:00Z`)) / 86_400_000);
}

